import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Image,
  Flex,
  SimpleGrid,
  Container,
  Badge,
  HStack,
  VStack,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import {
  FiCheck,
  FiArrowRight,
  FiClock,
  FiUserCheck,
  FiFileText,
  FiShield,
  FiZap,
} from "react-icons/fi";
import { apiUrl } from "../ApiUrl/ApiUrl";
import Slider from "react-slick";
import { Link, NavLink } from "react-router-dom";

function LegalResearchAssistance() {
  const [fashion, setFashion] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const packages = [
    {
      id: "702",
      title: "Single Legal Query Package",
      subtitle: "Dedicated Paralegal Researcher",
      price: 10000,
      queries: "1 In-Depth Legal Query",
      tat: "24-48 Hours",
      features: [
        "Binding Supreme Court & High Court Precedents",
        "Chronological Case Law Brief & Headnotes",
        "Cross-Jurisdictional Overruled Ratio Analysis",
        "Direct Counsel Telecon / Email Review",
      ],
      popular: false,
    },
    {
      id: "703",
      title: "Chambers 5-Query Retainer",
      subtitle: "Active Trial & Appellate Preparation",
      price: 45000,
      queries: "5 Priority Legal Queries",
      tat: "Within 24 Hours",
      features: [
        "All Single Query Features Included",
        "Priority Assignment to Senior Law Researchers",
        "Statutory Amendment & Notification Tracking",
        "Formatted Written Submission Drafts",
        "10% Savings on Single Query Rate",
      ],
      popular: true,
    },
    {
      id: "712",
      title: "Senior Counsel 10-Query Retainer",
      subtitle: "Full-Spectrum Chambers Paralegal Partner",
      price: 80000,
      queries: "10 Comprehensive Queries",
      tat: "Same-Day / 24-Hour Expedited",
      features: [
        "Dedicated Research Team on Standby",
        "Pre-Hearing Cause List Case Notes",
        "Commercial & Constitutional Bench Citations",
        "Unlimited Query Clarifications",
        "20% Retainer Cost Benefit",
      ],
      popular: false,
    },
  ];

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const getFashion = () => {
    setLoading(true);
    fetch(`${apiUrl}/public/allbooks`)
      .then((res) => res.json())
      .then((res) => setFashion(res))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getFashion();
  }, []);

  return (
    <Box bg="#F8FAFC" minH="100vh" pb={16}>
      {/* Hero Header */}
      <Box
        bgGradient="linear(to-b, #0B132B, #162237)"
        color="white"
        py={{ base: 12, md: 16 }}
        px={{ base: 4, md: 8 }}
        textAlign="center"
      >
        <Container maxW="900px">
          <Badge
            bg="whiteAlpha.200"
            color="#EAB308"
            px={3}
            py={1}
            rounded="full"
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wider"
            mb={4}
            textTransform="uppercase"
          >
            Human Expert + AI Research Desk
          </Badge>
          <Heading as="h1" size={{ base: "xl", md: "2xl" }} fontWeight="black" mb={4} letterSpacing="tight">
            Legal Research Assistance <br />
            <Text as="span" color="#FACC15">(Paralegal Partner)</Text>
          </Heading>
          <Text fontSize={{ base: "sm", md: "md" }} color="gray.300" maxW="700px" mx="auto" lineHeight="tall">
            Personalized, advocate-grade legal research conducted by experienced advocates and legal scholars. Receive comprehensive, citation-verified case law briefs delivered directly to your chambers within 24 hours.
          </Text>
        </Container>
      </Box>

      {/* Process Banner */}
      <Container maxW="7xl" px={{ base: 4, md: 8 }} mt={-8}>
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 6, md: 8 }}
          boxShadow="0 10px 30px rgba(0,0,0,0.06)"
          border="1px solid"
          borderColor="gray.100"
          mb={12}
        >
          <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="#EAB308" letterSpacing="wider" mb={2} textAlign="center">
            How Paralegal Partner Works
          </Text>
          <Heading as="h2" size="md" color="#162237" textAlign="center" mb={8}>
            A Four-Step Protocol Designed Around Court Deadlines
          </Heading>

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
            <VStack p={5} bg="#F8FAFC" borderRadius="xl" align="flex-start" spacing={3}>
              <Box p={2.5} bg="blue.50" color="#162237" borderRadius="lg">
                <Icon as={FiFileText} boxSize={5} />
              </Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.400">STEP 01</Text>
              <Text fontSize="sm" fontWeight="bold" color="#162237">Submit Your Query</Text>
              <Text fontSize="xs" color="gray.600" lineHeight="tall">
                Send your facts, propositions of law, or citation dilemmas via our confidential portal.
              </Text>
            </VStack>

            <VStack p={5} bg="#F8FAFC" borderRadius="xl" align="flex-start" spacing={3}>
              <Box p={2.5} bg="amber.50" color="#B45309" borderRadius="lg" bg="#FEF3C7">
                <Icon as={FiUserCheck} boxSize={5} />
              </Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.400">STEP 02</Text>
              <Text fontSize="sm" fontWeight="bold" color="#162237">Counsel Assignment</Text>
              <Text fontSize="xs" color="gray.600" lineHeight="tall">
                A subject specialist legal researcher reviews the proposition and initiates deep precedent analysis.
              </Text>
            </VStack>

            <VStack p={5} bg="#F8FAFC" borderRadius="xl" align="flex-start" spacing={3}>
              <Box p={2.5} bg="purple.50" color="#7C3AED" borderRadius="lg">
                <Icon as={FiZap} boxSize={5} />
              </Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.400">STEP 03</Text>
              <Text fontSize="sm" fontWeight="bold" color="#162237">Precedent Synthesis</Text>
              <Text fontSize="xs" color="gray.600" lineHeight="tall">
                We cross-examine latest Supreme Court and High Court ratios, confirming citations remain good law.
              </Text>
            </VStack>

            <VStack p={5} bg="#F8FAFC" borderRadius="xl" align="flex-start" spacing={3}>
              <Box p={2.5} bg="emerald.50" color="#059669" borderRadius="lg" bg="#D1FAE5">
                <Icon as={FiClock} boxSize={5} />
              </Box>
              <Text fontSize="xs" fontWeight="bold" color="gray.400">STEP 04</Text>
              <Text fontSize="sm" fontWeight="bold" color="#162237">24-Hour Dispatch</Text>
              <Text fontSize="xs" color="gray.600" lineHeight="tall">
                Receive an executive case brief with ratio decendi, headnotes, and PDF copies of pertinent rulings.
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>

        {/* Pricing Packages */}
        <VStack spacing={4} mb={8} textAlign="center">
          <Badge colorScheme="yellow" px={3} py={1} borderRadius="full" fontSize="xs">
            Query Retainer Plans
          </Badge>
          <Heading size="lg" color="#162237">
            Select Your Legal Research Retainer
          </Heading>
          <Text fontSize="sm" color="gray.500" maxW="2xl">
            All research briefs are drafted under advocate-client confidentiality and delivered ready for court submissions.
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={16}>
          {packages.map((pkg) => (
            <Box
              key={pkg.id}
              bg="white"
              borderRadius="2xl"
              border="2px solid"
              borderColor={pkg.popular ? "#162237" : "gray.100"}
              boxShadow={pkg.popular ? "xl" : "sm"}
              p={{ base: 6, md: 8 }}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              position="relative"
            >
              {pkg.popular && (
                <Badge
                  position="absolute"
                  top="-12px"
                  right="24px"
                  bg="#162237"
                  color="#FACC15"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="xs"
                  fontWeight="bold"
                  textTransform="uppercase"
                >
                  Most Popular
                </Badge>
              )}

              <Box>
                <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                  {pkg.subtitle}
                </Text>
                <Heading size="md" color="#162237" mt={1} mb={2}>
                  {pkg.title}
                </Heading>
                <HStack align="baseline" mb={4}>
                  <Text fontSize="2xl" fontWeight="black" color="#162237">
                    ₹{pkg.price.toLocaleString("en-IN")}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    + 18% GST
                  </Text>
                </HStack>

                <HStack spacing={2} p={2.5} bg="#F8FAFC" borderRadius="lg" mb={6}>
                  <Icon as={FiClock} color="#059669" boxSize={4} />
                  <Text fontSize="xs" fontWeight="semibold" color="#162237">
                    TAT: {pkg.tat}
                  </Text>
                </HStack>

                <VStack align="stretch" spacing={2.5} mb={8}>
                  {pkg.features.map((feat, idx) => (
                    <HStack key={idx} align="flex-start" spacing={2.5} fontSize="xs">
                      <Icon as={FiCheck} color="#059669" boxSize={4} mt={0.5} flexShrink={0} />
                      <Text color="gray.600">{feat}</Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Button
                as={Link}
                to={`/products/view/${pkg.id}`}
                size="lg"
                bg={pkg.popular ? "#162237" : "gray.100"}
                color={pkg.popular ? "white" : "#162237"}
                _hover={{
                  bg: pkg.popular ? "#0B132B" : "gray.200",
                  transform: "translateY(-1px)",
                }}
                rightIcon={<FiArrowRight />}
                w="full"
              >
                Purchase Retainer
              </Button>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Recommended Softwares Slider */}
      <Container maxW="7xl" px={{ base: 4, md: 8 }}>
        <HStack justify="space-between" align="flex-end" mb={6}>
          <Box>
            <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="#EAB308" letterSpacing="wider">
              Legal Tech Suite
            </Text>
            <Heading size="lg" color="#162237">
              Explore More Specialized Judicial Tools
            </Heading>
          </Box>
          <Button as={Link} to="/software" variant="ghost" color="#162237" rightIcon={<FiArrowRight />} size="sm">
            View All Software
          </Button>
        </HStack>

        <Box className="software-slick-slider">
          <Slider {...settings}>
            {fashion
              .filter((item) => item.maincatname === "software")
              .map((item) => (
                <Box key={item.id} px={2} pb={4}>
                  <Box
                    as={NavLink}
                    to={`/products/view/${item.id}`}
                    display="block"
                    bg="white"
                    p={4}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.100"
                    boxShadow="sm"
                    _hover={{ transform: "translateY(-4px)", boxShadow: "md", borderColor: "gray.300" }}
                    transition="all 0.2s"
                    h="full"
                  >
                    <Image
                      src={item.image1 || item.image || "./navneetlawstudio.png"}
                      alt={item.bookTitle}
                      h="140px"
                      w="full"
                      objectFit="contain"
                      mb={3}
                      p={2}
                    />
                    <Text fontSize="sm" fontWeight="bold" color="#162237" noOfLines={2} mb={1}>
                      {item.bookTitle}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                      {item.author || "Legal Software Edition"}
                    </Text>
                    <HStack justify="space-between" align="center">
                      <Text fontSize="sm" fontWeight="extrabold" color="#162237">
                        ₹{Number(item.price || 0).toLocaleString("en-IN")}
                      </Text>
                      <Button size="xs" colorScheme="blue" variant="outline">
                        View Details
                      </Button>
                    </HStack>
                  </Box>
                </Box>
              ))}
          </Slider>
        </Box>
      </Container>
    </Box>
  );
}

export default LegalResearchAssistance;
